import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { BookOpenCheck, CalendarClock, ClipboardCheck, FileText, RefreshCw, UserCog, UserX, Users } from "lucide-react";

import { DecisionsCard, DeskTiles, ViewerNote, type DeskTile } from "../../components/dashboard/DeskParts";
import { longDay, percent, shortDay } from "../../components/dashboard/deskLogic";
import { STAGES } from "../../components/examinations/examLogic";
import { ErrorState, LoadingState } from "../../components/PageState";
import { ClientRegister, StatusDot, type RegisterColumn } from "../../components/register/ClientRegister";
import { DetailsModal } from "../../components/register/DetailsModal";
import { classConfig, classStatus, studiesTotals, viewerNote, type StudiesClass, type StudiesDesk } from "../../components/studies/studiesLogic";
import { api } from "../../services/api";

/**
 * The Director of Studies' dashboard: what is waiting for them, each class's
 * teaching gaps and syllabus coverage this year, and the examinations
 * pipeline. It shows the same school to the head teacher, director or an
 * administrator who opens it.
 */
export default function DirectorOfStudiesDashboard() {
  const [viewing, setViewing] = useState<number | null>(null);
  const query = useQuery({
    queryKey: ["director-of-studies-desk"],
    queryFn: async () => (await api.get("/dashboard/director-of-studies/")).data as StudiesDesk,
    refetchInterval: 60_000,
  });

  if (query.isLoading) return <LoadingState label="Loading the Director of Studies' dashboard…" />;
  if (query.isError || !query.data) return <ErrorState title="Dashboard unavailable" message="The Director of Studies' dashboard could not be loaded. Check your connection and try again." retry={() => query.refetch()} />;

  const data = query.data;
  const totals = studiesTotals(data.classes);
  const stages = data.exams.stages;
  const inReview = stages.submitted + stages.moderated + stages.approved;

  const tiles: DeskTile[] = [
    { label: "Subjects without a teacher", value: String(totals.unassigned), note: totals.unassigned ? "Class and subject pairs the syllabi call for" : "Every subject has a teacher", link: "/timetable?tab=assignments", icon: UserX, alert: totals.unassigned > 0 },
    { label: "Not on the timetable", value: String(totals.untimetabled), note: totals.untimetabled ? "Subjects with no lesson in the week" : "Every subject has lessons", link: "/timetable", icon: CalendarClock, alert: totals.untimetabled > 0 },
    { label: "Classes without a class teacher", value: String(totals.noClassTeacher), note: `Of ${data.classes.length} classes this year`, link: "/class-setup?tab=teachers", icon: UserCog, alert: totals.noClassTeacher > 0 },
    { label: "Syllabus coverage", value: percent(totals.coverage), note: totals.behind ? `${totals.behind} topics running behind` : "Topics completed across all classes", link: "/curriculum", icon: BookOpenCheck, alert: totals.behind > 0 },
    { label: "Registers taken today", value: `${data.registers.marked} of ${data.registers.classes}`, note: data.registers.marked < data.registers.classes ? `${data.registers.classes - data.registers.marked} classes still to mark` : "Every class is marked", link: "/attendance", icon: ClipboardCheck, alert: data.registers.marked < data.registers.classes },
    { label: "Results in review", value: String(inReview), note: `${stages.draft} being marked · ${stages.published} published`, link: "/examinations", icon: FileText },
    { label: "Exams in the next two weeks", value: String(data.exams.upcoming.length), note: data.exams.upcoming[0] ? `Next: ${data.exams.upcoming[0].name}, ${shortDay(data.exams.upcoming[0].date)}` : "None timetabled", link: "/examinations?tab=periods", icon: Users },
  ];

  const columns: RegisterColumn<StudiesClass>[] = [
    { header: "Class", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name },
    { header: "Class teacher", cell: (row) => row.class_teacher || <span className="text-amber-700">None assigned</span>, exportValue: (row) => row.class_teacher },
    { header: "Learners", cell: (row) => row.learners, exportValue: (row) => row.learners, align: "right" },
    { header: "Subjects", cell: (row) => row.subjects || "—", exportValue: (row) => row.subjects, align: "right" },
    { header: "No teacher", cell: (row) => (row.unassigned.length ? <span className="font-semibold text-red-700">{row.unassigned.length}</span> : "—"), exportValue: (row) => row.unassigned.join("; "), align: "right" },
    { header: "Not timetabled", cell: (row) => (row.untimetabled.length ? <span className="font-semibold text-amber-800">{row.untimetabled.length}</span> : "—"), exportValue: (row) => row.untimetabled.join("; "), align: "right" },
    { header: "Lessons a week", cell: (row) => row.lessons_per_week, exportValue: (row) => row.lessons_per_week, align: "right" },
    { header: "Coverage", cell: (row) => (row.coverage === null ? "—" : `${percent(row.coverage)}${row.behind ? ` · ${row.behind} behind` : ""}`), exportValue: (row) => (row.coverage === null ? "" : `${row.coverage}%`), align: "right" },
    { header: "Status", cell: (row) => <StatusDot {...classStatus(row)} />, exportValue: (row) => classStatus(row).label },
  ];
  const row = viewing ? data.classes.find((item) => item.id === viewing) : undefined;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Director of Studies</h1>
          <p className="mt-1 text-sm text-gray-500">{longDay(data.today)}{data.academic_year ? ` · academic year ${data.academic_year}` : ""} · teaching, coverage and examinations across the school.</p>
        </div>
        <button type="button" onClick={() => query.refetch()} className="btn btn-outline gap-2">
          <RefreshCw className={`h-4 w-4 ${query.isFetching ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <ViewerNote text={viewerNote(data.viewer_role)} />
      {!data.academic_year && <p className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900">No academic year is marked current, so there are no classes to show. Set one in Academics.</p>}

      <DecisionsCard decisions={data.decisions} />
      <DeskTiles tiles={tiles} />

      <ClientRegister
        title="Classes this year"
        description="Each class against the syllabi for its level: subjects with no teacher assigned or no lesson on the timetable, and how far it has got. Open a class for the subjects by name."
        actions={<Link to="/timetable?tab=assignments" className="btn btn-outline h-9 text-sm">Teacher assignments</Link>}
        rows={data.classes}
        config={classConfig}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        openLabel={(item) => (item.unassigned.length || item.untimetabled.length ? "See gaps" : "Open")}
        noun={["class", "classes"]}
        exportName={{ file: `classes-${data.academic_year || "this-year"}`, title: `Classes and teaching gaps, ${data.academic_year}` }}
        emptyText="There are no classes in the current academic year."
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="card">
          <div className="card-titlebar"><h2 className="font-semibold text-gray-900">Topics running behind</h2><Link to="/curriculum" className="text-xs font-medium text-primary-700 hover:underline">Coverage</Link></div>
          {data.behind.length ? (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500"><th className="px-6 py-2 font-medium">Class and subject</th><th className="px-2 py-2 text-right font-medium">Behind</th><th className="px-6 py-2 text-right font-medium">Covered</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {data.behind.map((item) => (
                  <tr key={`${item.class_name}-${item.subject_name}`}>
                    <td className="px-6 py-2"><span className="font-medium text-gray-900">{item.class_name} {item.subject_name}</span>{item.teachers.length > 0 && <span className="block text-xs text-gray-500">{item.teachers.join(", ")}</span>}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-semibold text-amber-800">{item.behind}</td>
                    <td className="px-6 py-2 text-right tabular-nums text-gray-700">{percent(item.coverage)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="px-6 py-6 text-center text-sm text-gray-500">No topic is running past the weeks planned for it.</p>}
        </section>

        <section className="card">
          <div className="card-titlebar"><h2 className="font-semibold text-gray-900">Examinations this year</h2><Link to="/examinations" className="text-xs font-medium text-primary-700 hover:underline">Examinations</Link></div>
          <div className="grid grid-cols-5 divide-x divide-gray-100 border-b border-gray-100">
            {STAGES.map((stage) => (
              <div key={stage.key} className="px-3 py-3 text-center">
                <p className="text-lg font-semibold tabular-nums text-gray-900">{stages[stage.key]}</p>
                <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] leading-tight text-gray-500"><span className={`h-1.5 w-1.5 shrink-0 rounded-full ${stage.dot}`} />{stage.label}</p>
              </div>
            ))}
          </div>
          <h3 className="px-6 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Next two weeks</h3>
          {data.exams.upcoming.length ? (
            <ul className="divide-y divide-gray-100">
              {data.exams.upcoming.map((exam) => (
                <li key={exam.id} className="flex justify-between gap-3 px-6 py-2.5 text-sm">
                  <span><span className="block font-medium text-gray-900">{exam.name}</span><span className="text-xs text-gray-500">{exam.subject}{exam.classes.length ? ` · ${exam.classes.join(", ")}` : ""}</span></span>
                  <span className="whitespace-nowrap text-xs text-gray-500">{shortDay(exam.date)} · {exam.start_time.slice(0, 5)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="px-6 pb-6 pt-2 text-sm text-gray-500">No papers in the next two weeks.</p>}
        </section>
      </div>

      {row && (
        <DetailsModal
          title={row.name}
          headline={{ label: `${row.level} · ${row.learners} learners`, value: row.coverage === null ? "No syllabus yet" : `${percent(row.coverage)} covered`, note: row.topics ? `${row.topics_done} of ${row.topics} topics completed${row.behind ? `, ${row.behind} running behind` : ""}` : "No syllabus has been written for this level" }}
          status={classStatus(row)}
          facts={[["Class teacher", row.class_teacher || "None assigned"], ["Subjects in the syllabi", String(row.subjects)], ["Lessons a week", String(row.lessons_per_week)]]}
          sections={[
            { title: "Subjects with no teacher assigned", body: row.unassigned.join(", "), tone: "danger" },
            { title: "Subjects with no lesson on the timetable", body: row.untimetabled.join(", "), tone: "warning" },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline mr-auto" onClick={() => setViewing(null)}>Close</button>
              {!row.class_teacher && <Link to="/class-setup?tab=teachers" className="btn btn-outline">Choose a class teacher</Link>}
              {row.untimetabled.length > 0 && <Link to="/timetable" className="btn btn-outline">Open the timetable</Link>}
              {row.unassigned.length > 0 && <Link to="/timetable?tab=assignments" className="btn btn-primary">Assign teachers</Link>}
            </>
          }
        />
      )}
    </div>
  );
}
